import {
    AtRule,
    Declaration,
    Root,
    Rule,
} from "postcss";
import safeParser from "postcss-safe-parser";
import valueParser from "postcss-value-parser";

const SAFE_AT_RULES = new Set([
    "media",
    "supports",
]);

/**
 * Legacy CSS features that can execute script
 * in old/legacy browsers.
 */
const XSS_RISK_PROPERTIES = new Set([
    "behavior",
    "-moz-binding",
    "binding",
]);

const ALLOWED_PROTOCOLS = new Set([
    "https:",
    "http:",
]);

/**
 * Remove surrounding quotes.
 */
function stripQuotes(value: string): string {
    return value.replace(/^['"]|['"]$/g, "").trim();
}

/**
 * Decode CSS escapes.
 *
 * Example:
 *   \6a avascript: -> javascript:
 */
function decodeCssEscapes(input: string): string {
    return input.replace(
        /\\([0-9a-fA-F]{1,6}\s?|.)/g,
        (_, escaped: string) => {
            const hex = escaped.trim();

            if (/^[0-9a-fA-F]{1,6}$/.test(hex)) {
                return String.fromCodePoint(parseInt(hex, 16));
            }

            return escaped;
        }
    );
}

/**
 * Validate a URL found inside CSS.
 */
function isSafeUrl(rawUrl: string): boolean {
    const cleaned = decodeCssEscapes(
        stripQuotes(rawUrl)
    ).trim();

    /**
     * FIX: Reject empty url() — resolves to origin and
     * passes the https: protocol check, which is a logic
     * error. Empty URLs serve no purpose.
     */
    if (cleaned === "") {
        return false;
    }

    try {
        /**
         * Explicitly reject dangerous schemes before URL parsing.
         * This catches:
         *   javascript:
         *   data:
         *   vbscript:
         */
        const lowered = cleaned.toLowerCase();

        if (
            lowered.startsWith("javascript:") ||
            lowered.startsWith("data:") ||
            lowered.startsWith("vbscript:")
        ) {
            return false;
        }

        /**
         * Resolve relative URLs safely.
         */
        const parsed = new URL(
            cleaned,
            window.location.origin
        );

        return ALLOWED_PROTOCOLS.has(parsed.protocol);
    } catch {
        return false;
    }
}

/**
 * Detect dangerous CSS values.
 */
function hasUnsafeValue(value: string): boolean {
    let unsafe = false;

    const parsed = valueParser(value);

    parsed.walk((node) => {
        if (node.type !== "function") {
            return;
        }

        const fn = node;

        const fnName = decodeCssEscapes(
            fn.value.toLowerCase()
        );

        /**
         * Block expression()
         */
        if (fnName === "expression") {
            unsafe = true;
            return false;
        }

        /**
         * Validate every url()
         */
        if (fnName === "url") {
            const urlValue = valueParser
                .stringify(fn.nodes)
                .trim();

            if (!isSafeUrl(urlValue)) {
                unsafe = true;
                return false;
            }
        }
    });

    return unsafe;
}

/**
 * FIX: Check @supports condition strings for unsafe url() references.
 *
 * PostCSS parses @supports conditions as raw strings (atRule.params),
 * not as declarations, so the declaration walker never sees them.
 * A rule like:
 *   @supports (background: url(javascript:alert(1))) { ... }
 * would previously pass through unsanitized.
 */
function hasUnsafeSupportsCondition(params: string): boolean {
    return hasUnsafeValue(params);
}

/**
 * Scope selectors safely.
 */
function scopeSelector(
    selector: string,
    scope: string
): string {
    selector = selector.trim();

    /**
     * Preserve root selector meaning.
     */
    if (
        selector === "html" ||
        selector === "body" ||
        selector === ":root"
    ) {
        return scope;
    }

    return `${scope} ${selector}`;
}

export function scopeAndImportantCSS(
    css: string,
    scope = ".user-content"
): string {
    const root: Root = safeParser(css);

    root.walk(node => {
        /**
         * Remove unsafe at-rules.
         */
        if (node.type === "atrule") {
            const atRule = node as AtRule;

            const name = atRule.name.toLowerCase();

            if (!SAFE_AT_RULES.has(name)) {
                atRule.remove();
                return;
            }

            /**
             * FIX: Validate @supports condition params for unsafe url().
             * @media params (e.g. screen, print, min-width) cannot contain
             * url() so only @supports needs this check.
             */
            if (
                name === "supports" &&
                hasUnsafeSupportsCondition(atRule.params)
            ) {
                atRule.remove();
                return;
            }
        }

        /**
         * Sanitize declarations.
         */
        if (node.type === "decl") {
            const decl = node as Declaration;

            const prop = decodeCssEscapes(
                decl.prop.toLowerCase()
            );

            if (XSS_RISK_PROPERTIES.has(prop)) {
                decl.remove();
                return;
            }

            if (hasUnsafeValue(decl.value)) {
                decl.remove();
                return;
            }
        }

        /**
         * Scope selectors + force !important
         */
        if (node.type === "rule") {
            const rule = node as Rule;

            rule.selectors = rule.selectors.map(sel =>
                scopeSelector(sel, scope)
            );

            rule.walkDecls(decl => {
                decl.important = true;
            });
        }
    });
    

    return root.toString();
}
